import { useRef, type ReactNode } from 'react';

import type { Operation } from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import {
  formatCpfCnpj,
  formatDate,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Badge } from '@/shared/ui/Badge';
import type { Column } from '@/shared/ui/DataTable';
import { DataTable } from '@/shared/ui/DataTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useEncaixePelaLargura } from '@/shared/ui/useEncaixePelaLargura';

import {
  encaixarListaDeOperacoes,
  type EncaixeDaListaDeOperacoes,
} from './listaDeOperacoesLayout';

/** O que as acoes da linha precisam saber da pagina. */
interface AcoesDaOperacao {
  podeImprimir: boolean;
  onVisualizar: (operacao: Operation) => void;
  onImprimir: (operacao: Operation) => void;
}

/** Texto em uma linha so: o que nao cabe vira reticencias e aparece no mouse. */
function Linha({
  children,
  className,
  titulo,
}: {
  children: ReactNode;
  className?: string;
  titulo?: string | undefined;
}) {
  return (
    <span
      title={titulo ?? (typeof children === 'string' ? children : undefined)}
      className={cn('block truncate', className)}
    >
      {children}
    </span>
  );
}

function Detalhe({
  children,
  titulo,
}: {
  children: ReactNode;
  titulo?: string | undefined;
}) {
  return (
    <Linha className="text-2xs text-ink-500" titulo={titulo}>
      {children}
    </Linha>
  );
}

function numeroDaCotacao(operacao: Operation): string {
  return operacao.proposal_number ?? 'sem numero';
}

/** Numero da cotacao e, embaixo, o detalhe que a disposicao escolhe. */
function Cotacao({
  operacao,
  detalhe,
  titulo,
}: {
  operacao: Operation;
  detalhe: ReactNode;
  titulo?: string | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="flex min-w-0 items-center gap-2 font-medium text-ink-900">
        <span className="truncate tabular-nums">{numeroDaCotacao(operacao)}</span>
        {operacao.is_overdue ? <Badge tone="warning">Prazo vencido</Badge> : null}
      </p>
      <Detalhe titulo={titulo}>{detalhe}</Detalhe>
    </div>
  );
}

/** "Operacao N - objeto", com o numero no proprio elemento, como era na lista. */
function OperacaoEObjeto({ operacao }: { operacao: Operation }) {
  return (
    <>
      Operacao <span className="tabular-nums">{operacao.number}</span>
      {operacao.subject ? ` - ${operacao.subject}` : ''}
    </>
  );
}

function textoDaOperacao(operacao: Operation): string {
  return `Operacao ${operacao.number}${operacao.subject ? ` - ${operacao.subject}` : ''}`;
}

function Tomador({ operacao }: { operacao: Operation }) {
  const documento = operacao.policyholder.document
    ? formatCpfCnpj(operacao.policyholder.document)
    : '';
  return (
    <div className="min-w-0">
      <Linha>{operacao.policyholder.label}</Linha>
      {documento ? <Detalhe>{documento}</Detalhe> : null}
    </div>
  );
}

function tipoDaCotacao(operacao: Operation): string {
  return statusLabel('tipoCotacao', operacao.proposal_type ?? 'apolice');
}

function nomeDoCorretor(operacao: Operation): string {
  return operacao.broker?.label ?? 'direto';
}

function prazoDaEtapa(operacao: Operation): string {
  return operacao.stage_due_at ? formatDate(operacao.stage_due_at) : 'sem prazo';
}

/**
 * Status da cotacao e etapa da esteira, um em cima do outro. Com o prazo, a etapa vem
 * com o prazo embaixo no lugar do status: tres linhas nao cabem na altura da linha.
 */
function Situacao({ operacao, comPrazo }: { operacao: Operation; comPrazo?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      {!comPrazo && operacao.proposal_status ? (
        <StatusBadge kind="cotacao" value={operacao.proposal_status} />
      ) : null}
      <StatusBadge kind="operacao" value={operacao.stage} />
      {comPrazo ? <Detalhe>{`Prazo ${prazoDaEtapa(operacao)}`}</Detalhe> : null}
    </div>
  );
}

/** IS, com premio e taxa logo abaixo. */
function Valores({ operacao, comTaxa }: { operacao: Operation; comTaxa?: boolean }) {
  return (
    <div className="min-w-0 text-right">
      <Linha className="tabular-nums">{formatMoney(operacao.insured_amount)}</Linha>
      <Detalhe>
        Premio {formatMoney(operacao.premium)}
        {comTaxa ? ` · ${formatRate(operacao.applied_rate_bps)}` : ''}
      </Detalhe>
    </div>
  );
}

/**
 * As duas acoes da linha, em icones lado a lado: o olho de visualizar e a impressora da
 * minuta, com o nome na dica e no leitor de tela. O clique para aqui e nao abre a
 * cotacao pela linha.
 */
function Acoes({ operacao, acoes }: { operacao: Operation; acoes: AcoesDaOperacao }) {
  const numero = numeroDaCotacao(operacao);
  return (
    <div className="flex items-center gap-1">
      <AcaoComIcone
        acao="visualizar"
        rotulo="Visualizar cotacao"
        aria-label={`Visualizar cotacao ${operacao.proposal_number ?? operacao.number}`}
        onClick={(event) => {
          event.stopPropagation();
          acoes.onVisualizar(operacao);
        }}
      />
      {acoes.podeImprimir ? (
        <AcaoComIcone
          acao="imprimir"
          rotulo="Imprimir minuta"
          aria-label={`Imprimir minuta da cotacao ${operacao.proposal_number ?? numero}`}
          onClick={(event) => {
            event.stopPropagation();
            acoes.onImprimir(operacao);
          }}
        />
      ) : null}
    </div>
  );
}

/** Dois icones de 32 px com o vao entre eles, mais o respiro da celula. */
const LARGURA_DAS_ACOES = '6.5rem';

function colunaDasAcoes(acoes: AcoesDaOperacao) {
  return {
    key: 'acoes',
    header: 'Acoes',
    width: LARGURA_DAS_ACOES,
    render: (operacao: Operation) => <Acoes operacao={operacao} acoes={acoes} />,
  } satisfies Column<Operation>;
}

function colunasAmplas(acoes: AcoesDaOperacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Cotacao no',
      width: '10.5rem',
      render: (operacao) => (
        <Cotacao
          operacao={operacao}
          detalhe={<OperacaoEObjeto operacao={operacao} />}
          titulo={textoDaOperacao(operacao)}
        />
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '4.75rem',
      render: (operacao) => <Linha>{tipoDaCotacao(operacao)}</Linha>,
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => <Tomador operacao={operacao} />,
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      width: '13%',
      render: (operacao) => <Linha>{operacao.modality.label}</Linha>,
    },
    {
      key: 'data',
      header: 'Data da cotacao',
      width: '6.25rem',
      render: (operacao) => <Linha>{formatDate(operacao.created_at)}</Linha>,
    },
    {
      key: 'valor',
      header: 'IS',
      detail: 'Premio',
      align: 'right',
      width: '8.5rem',
      render: (operacao) => <Valores operacao={operacao} />,
    },
    {
      key: 'taxa',
      header: 'Taxa',
      align: 'right',
      width: '4.25rem',
      render: (operacao) => (
        <Linha className="tabular-nums">{formatRate(operacao.applied_rate_bps)}</Linha>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      detail: 'Etapa',
      width: '9.5rem',
      render: (operacao) => <Situacao operacao={operacao} />,
    },
    {
      key: 'corretor',
      header: 'Corretor',
      width: '10%',
      render: (operacao) => <Linha>{nomeDoCorretor(operacao)}</Linha>,
    },
    {
      key: 'prazo',
      header: 'Prazo da etapa',
      width: '6.25rem',
      render: (operacao) => <Linha>{prazoDaEtapa(operacao)}</Linha>,
    },
    colunaDasAcoes(acoes),
  ];
}

function colunasReunidas(acoes: AcoesDaOperacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Cotacao no',
      detail: 'Operacao e data',
      width: '11rem',
      render: (operacao) => (
        <Cotacao
          operacao={operacao}
          detalhe={`Operacao ${operacao.number} em ${formatDate(operacao.created_at)}`}
        />
      ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => <Tomador operacao={operacao} />,
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      detail: 'Tipo e corretor',
      width: '18%',
      render: (operacao) => (
        <div className="min-w-0">
          <Linha>{operacao.modality.label}</Linha>
          <Detalhe>{`${tipoDaCotacao(operacao)} · ${nomeDoCorretor(operacao)}`}</Detalhe>
        </div>
      ),
    },
    {
      key: 'valor',
      header: 'IS',
      detail: 'Premio e taxa',
      align: 'right',
      width: '10rem',
      render: (operacao) => <Valores operacao={operacao} comTaxa />,
    },
    {
      key: 'status',
      header: 'Status',
      detail: 'Etapa e prazo',
      width: '10rem',
      render: (operacao) => <Situacao operacao={operacao} comPrazo />,
    },
    colunaDasAcoes(acoes),
  ];
}

function colunasEstreitas(acoes: AcoesDaOperacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Cotacao no',
      detail: 'Tomador',
      render: (operacao) => (
        <Cotacao operacao={operacao} detalhe={operacao.policyholder.label} />
      ),
    },
    {
      key: 'valor',
      header: 'IS',
      detail: 'Premio',
      align: 'right',
      width: '8.75rem',
      render: (operacao) => <Valores operacao={operacao} />,
    },
    {
      key: 'status',
      header: 'Status',
      detail: 'Etapa',
      width: '9.5rem',
      render: (operacao) => <Situacao operacao={operacao} />,
    },
    colunaDasAcoes(acoes),
  ];
}

function colunasMinimas(acoes: AcoesDaOperacao): Column<Operation>[] {
  return [
    {
      key: 'numero',
      header: 'Cotacao no',
      detail: 'Tomador e IS',
      render: (operacao) => (
        <div className="min-w-0">
          <Linha className="font-medium text-ink-900 tabular-nums">
            {numeroDaCotacao(operacao)}
          </Linha>
          <Detalhe>{`${operacao.policyholder.label} · ${formatMoney(operacao.insured_amount)}`}</Detalhe>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Etapa',
      width: '7.5rem',
      render: (operacao) => <StatusBadge kind="operacao" value={operacao.stage} />,
    },
    colunaDasAcoes(acoes),
  ];
}

const colunasDoEncaixe: Record<
  EncaixeDaListaDeOperacoes,
  (acoes: AcoesDaOperacao) => Column<Operation>[]
> = {
  ampla: colunasAmplas,
  reunida: colunasReunidas,
  estreita: colunasEstreitas,
  minima: colunasMinimas,
};

/**
 * A lista de operacoes na disposicao que cabe na largura recebida, com as acoes sempre
 * na ultima coluna. Ver listaDeOperacoesLayout.ts.
 */
export function ListaDeOperacoes({
  items,
  loading,
  empty,
  onAbrir,
  acoes,
}: {
  items: Operation[];
  loading: boolean;
  empty: ReactNode;
  onAbrir: (operacao: Operation) => void;
  acoes: AcoesDaOperacao;
}) {
  const area = useRef<HTMLDivElement>(null);
  const encaixe = useEncaixePelaLargura(area, encaixarListaDeOperacoes);
  return (
    <div ref={area}>
      <DataTable
        fluida
        columns={colunasDoEncaixe[encaixe](acoes)}
        items={items}
        keyOf={(operacao) => operacao.id}
        loading={loading}
        onRowClick={onAbrir}
        empty={empty}
      />
    </div>
  );
}
